import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, json } from '@/lib/api';
import { captureInvitation, clearInvitation, readInvitation } from '@/lib/invitation';
import { useSession } from './session';
import './auth.css';
import './households.css';

export function InvitationScreen({ configured, initialToken }: { configured: boolean; initialToken?: string }) {
  const session = useSession();
  const [entry, setEntry] = useState(() => ({ token: initialToken || readInvitation(), revision: 0 }));
  useEffect(() => {
    const consume = () => {
      if (location.pathname !== '/invite' || !location.hash) return;
      const token = captureInvitation(location.href);
      history.replaceState(history.state, '', '/invite');
      setEntry(previous => ({ token, revision: previous.revision + 1 }));
    };
    consume(); addEventListener('hashchange', consume);
    return () => removeEventListener('hashchange', consume);
  }, []);
  return <InvitationContent key={`${entry.revision}:${entry.token || ''}:${session.data?.user.id || 'signed-out'}`} configured={configured} token={entry.token} />;
}
function InvitationContent({ configured, token }: { configured: boolean; token?: string }) {
  const session = useSession(), who = session.data?.user.id;
  const [preview, setPreview] = useState<{ name: string; householdId: string }>();
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [name, setName] = useState(''), [error, setError] = useState('');
  const [busy, setBusy] = useState(false), [joined, setJoined] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    setPreview(undefined); setError('');
    if (!token || !who || !session.signedIn) return;
    const controller = new AbortController();
    void api<{ name: string; householdId: string }>('/invitations/preview', { method: 'POST', body: json({ token }), signal: controller.signal })
      .then(value => { if (!controller.signal.aborted) setPreview(value); })
      .catch(cause => { if (!controller.signal.aborted) setError(cause.message); });
    return () => controller.abort();
  }, [token, who, session.signedIn, previewAttempt]);
  async function join(event: React.FormEvent) {
    event.preventDefault(); if (busy || !preview || !name.trim()) return;
    setBusy(true); setError('');
    try {
      const result = await api<{ joined: boolean }>('/invitations/accept', { method: 'POST', body: json({ token, name: name.trim() }) });
      if (!result.joined) throw new Error('The household could not be joined.');
      if (alive.current) {
        clearInvitation(); setJoined(true);
        await session.refresh();
      }
    } catch (cause) { if (alive.current) setError(cause instanceof Error ? cause.message : 'Could not join. Try again.'); }
    finally { if (alive.current) setBusy(false); }
  }
  return <main className="auth-screen">
    <a className="auth-brand" href="/">streamit<span>.</span></a>
    <section className="household-invitation" aria-labelledby="invite-title">
      <h1 id="invite-title">Join a household</h1>
      {joined ? <><p role="status">You joined {preview?.name || 'the household'}. Choose one of its profiles to start watching.</p><a href="/#settings">Open Streamit</a></> : !configured ? <p>Sign-in is being configured. Try this invitation again later.</p>
        : !token ? <p>Open the original invitation link. If you just signed in and this browser blocks storage, open that link again in the same browser.</p>
        : !session.signedIn ? <><p>Sign in, then review the household before joining.</p><a className="invite-sign-in" href="/sign-in?return_to=%2Finvite">Sign in to continue</a></>
        : session.error ? <p role="alert">{session.error.message}</p>
        : <>{error && <p role="alert" className="settings-error">{error}</p>}{preview ? <form onSubmit={join}>
          <h2>{preview.name}</h2><p>Joining gives you access to this household’s profiles and shared library. Its owner can manage your membership. TV remote access is granted separately.</p>
          <label htmlFor="invite-name">Your name in this household</label><Input id="invite-name" autoComplete="nickname" value={name} onChange={e => setName(e.target.value)} maxLength={64} required disabled={busy} />
          <Button type="submit" disabled={busy || !name.trim()}>{busy ? 'Joining…' : 'Join household'}</Button>
        </form> : error ? <Button type="button" variant="outline" onClick={() => { setError(''); setPreviewAttempt(value => value + 1); }}>Try again</Button> : <p role="status">Checking invitation…</p>}</>}
    </section>
    <nav aria-label="Invitation links"><a href="/">Back to Streamit</a><a href="/privacy.html">Privacy</a></nav>
  </main>;
}
